import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AppRoutes } from '@app/core/routing';
import {
  AuthService,
  DashboardUserType,
  Logout,
  Populate,
  SetDashboardUserType,
  UserState,
} from '@app/state';
import { Store } from '@ngxs/store';
import { UntilDestroy } from '@ngneat/until-destroy';
import { untilDestroyed } from '@ngneat/until-destroy';

/** Actions shared across header components that aren't all `HeaderBase` subclasses. */
@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class HeaderActionsService {
  private router = inject(Router);
  private authService = inject(AuthService);
  private store = inject(Store);

  /** JWT cookie is the source of truth; the store may not be populated yet. */
  get isAuthenticated(): boolean {
    return this.authService.authenticated;
  }

  fundraiseWithAi(onStart?: () => void): void {
    onStart?.();

    this.router.navigateByUrl(AppRoutes.create.campaign());
  }

  /**
   * @description
   * Sends the visitor to sign-in, returning them to `returnUrl` (defaults to the
   * page they were on).
   */
  goToLogin(returnUrl?: string): void {
    this.router.navigate([AppRoutes.auth.login()], {
      queryParams: { returnUrl: returnUrl ?? this.router.url },
    });
  }

  /** @description Clears the session. */
  logout(): void {
    this.store.dispatch(new Logout());
  }

  /**
   * @description
   * Switches the dashboard persona and lands on the dashboard overview.
   */
  setDashboardUserType(type: DashboardUserType): void {
    this.store.dispatch(new SetDashboardUserType(type));
    this.router.navigate([AppRoutes.dashboard.overview()]);
  }

  /**
   * @description
   * Opens the charity dashboard in a new tab, sending signed-out visitors to sign-in first.
   */
  goToCharityDashboard(dashboardUrl: string): void {
    window.open(dashboardUrl, '_blank', 'noopener,noreferrer');
  }

  populateUserIfNeeded(isBrowser: boolean, setLoading: (loading: boolean) => void): void {
    if (
      !isBrowser ||
      !this.authService.authenticated ||
      this.store.selectSnapshot(UserState.user)
    ) {
      return;
    }

    setLoading(true);
    this.store
      .dispatch(new Populate())
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => setLoading(false),
        error: () => setLoading(false),
      });
  }
}
